import React, { useState } from "react";
import { createPortal } from "react-dom";
import styles from "./ConsentPopup.module.css";

/**
 * ConsentPopup component - displays a consent modal for personalization services
 *
 * @param {Object} props
 * @param {Function} props.onAccept - Callback when user accepts consent
 * @param {Function} props.onDecline - Callback when user declines consent
 * @param {Function} props.onClose - Callback when user closes the popup (X button)
 * @param {boolean} props.loading - Whether the consent is being saved
 */
export const ConsentPopup = ({
  onAccept,
  onDecline,
  onClose,
  loading = false,
}) => {
  const handleAccept = () => {
    if (!loading) {
      onAccept();
    }
  };

  const handleDecline = () => {
    if (!loading) {
      onDecline();
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not the modal content
    if (e.target.className === styles.overlay) {
      onClose();
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <button
          className={`${styles.closeButton} custom_click_track`}
          data-custom_click_track_value="Beverage Consent Popup|Close|internal"
          onClick={onClose}
          aria-label="Close consent popup"
          disabled={loading}
        >
          ✕
        </button>

        <h2 className={styles.title}>
          Personalize your stay?
        </h2>

        <p className={styles.text}>We can use your preferences to tailor your experience at Marriott properties—like room details, amenities, and recommendations—so your stay feels more like you.</p>

        <div className={styles.buttonContainer}>
          <button
            className={`${styles.declineButton} custom_click_track`}
            data-custom_click_track_value="Beverage Consent Popup|No Thank You|internal"
            onClick={handleDecline}
            disabled={loading}
          >
            {loading ? "Saving..." : "No, Thank You"}
          </button>
          <button
            className={`${styles.acceptButton} custom_click_track`}
            data-custom_click_track_value="Beverage Consent Popup|Yes Please|internal"
            onClick={handleAccept}
            disabled={loading}
          >
            {loading ? "Saving..." : "Yes, Please"}
          </button>
        </div>

        <p className={styles.consentText}>
          Preferences can be
          updated at any time in my{" "}
          <a
            href="https://www.marriott.com/loyalty/myAccount/default.mi"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Bonvoy Account Profile
          </a>
          , and I can learn more about how my data is collected and used in the{" "}
          <a
            href="https://www.marriott.com/about/privacy.mi"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Global Privacy Statement
          </a>
          .
        </p>
      </div>
    </div>,
    document.body,
  );
};

export default ConsentPopup;
